import { isAxiosError } from 'axios';
import { isRequestCanceledError, parseResponseError } from '@/utils/api';
import { sanitizeDiagnosticText } from '@/utils/sanitize';

const STAGING_HOSTNAME_PATTERN = /stg|stag|hml|homolog/i;
const NUMERIC_SEGMENT_PATTERN = /^\d+$/;
const UUID_SEGMENT_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const classifyApiError = (error) => {
  if (isRequestCanceledError(error)) return 'canceled';

  if (!isAxiosError(error)) return 'unknown_error';

  if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) return 'timeout';

  const status = error.response?.status;

  if (!status) return 'network';
  if (status >= 400 && status < 500) return 'client_error';
  if (status >= 500 && status < 600) return 'server_error';

  return 'unknown_error';
};

export const getEnvironmentLabel = () => {
  if (process.env.NODE_ENV !== 'production') return process.env.NODE_ENV || 'development';

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const apiUrl = process.env.REACT_APP_API_URL || '';

  if (STAGING_HOSTNAME_PATTERN.test(hostname) || STAGING_HOSTNAME_PATTERN.test(apiUrl)) {
    return 'staging';
  }

  return 'production';
};

export const normalizeEndpoint = (url) => {
  if (typeof url !== 'string' || !url) return '';

  const withoutQuery = url.split(/[?#]/)[0];

  return withoutQuery
    .split('/')
    .map((segment) => {
      if (NUMERIC_SEGMENT_PATTERN.test(segment) || UUID_SEGMENT_PATTERN.test(segment)) {
        return ':id';
      }
      return segment;
    })
    .join('/');
};

export const buildApiErrorLogEntry = (error, { httpClient } = {}) => ({
  timestamp: new Date().toISOString(),
  environment: getEnvironmentLabel(),
  httpClient: httpClient || 'unknown',
  method: error?.config?.method ? error.config.method.toUpperCase() : 'UNKNOWN',
  endpoint: normalizeEndpoint(error?.config?.url),
  status: error?.response?.status ?? null,
  errorType: classifyApiError(error),
  message: sanitizeDiagnosticText(parseResponseError(error).message),
});

export const logApiError = (error, { httpClient } = {}) => {
  try {
    if (isRequestCanceledError(error)) return;

    const entry = buildApiErrorLogEntry(error, { httpClient });

    // eslint-disable-next-line no-console
    console.error('[ApiErrorLogger]', entry);
  } catch {
    // logging nunca deve interromper o tratamento de erro já feito pelas telas
  }
};
